import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Activity, Target, Heart, CheckCircle, Star, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function MovementAssessmentGuide() {
  const navigate = useNavigate();
  
  // State for interactive elements
  const [fitnessRatings, setFitnessRatings] = useState({
    Strength: null,
    Flexibility: null,
    "Cardio Endurance": null,
    Balance: null
  });
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [selectedBarriers, setSelectedBarriers] = useState([]);
  const [weekGoal, setWeekGoal] = useState('');

  const fitnessAreas = [
    { area: "Strength", questions: ["Can you do 10 push-ups (wall or floor)?", "Can you hold a plank for 30 seconds?", "Can you stand from sitting without using hands?"], why: "Muscle strength prevents falls, supports joints, and maintains metabolism" },
    { area: "Flexibility", questions: ["Can you touch your toes?", "Can you look over each shoulder easily?", "Can you clasp hands behind your back?"], why: "Flexibility prevents injury and reduces everyday aches and pains" },
    { area: "Cardio Endurance", questions: ["Can you walk briskly for 30 min without stopping?", "Do you get winded climbing stairs?", "Can you carry on a conversation while walking fast?"], why: "Cardiovascular health protects heart, brain, and longevity" },
    { area: "Balance", questions: ["Can you stand on one foot for 30 seconds?", "Can you walk heel-to-toe in a line?", "Do you feel steady on uneven surfaces?"], why: "Balance prevents falls—the leading cause of injury after 40" }
  ];

  const enjoyableActivities = [
    { activity: "Walking", benefits: "Low impact, social, outdoors" },
    { activity: "Swimming", benefits: "Zero impact, full body" },
    { activity: "Dancing", benefits: "Fun, social, cardio + coordination" },
    { activity: "Cycling", benefits: "Low impact, builds leg strength" },
    { activity: "Yoga", benefits: "Flexibility, strength, mindfulness" },
    { activity: "Hiking", benefits: "Nature, varied terrain, exploration" },
    { activity: "Tennis/Pickleball", benefits: "Social, cardio, reflexes" },
    { activity: "Gardening", benefits: "Functional movement, outdoors" },
    { activity: "Pilates", benefits: "Core strength, flexibility, posture" },
    { activity: "Weight Training", benefits: "Build muscle, boost metabolism" },
    { activity: "Group Fitness Classes", benefits: "Social, structured, motivating" },
    { activity: "Stretching/Foam Rolling", benefits: "Recovery, flexibility, relaxation" }
  ];

  const barriers = [
    "Not enough time", "Too tired", "Don't enjoy it", "Physical limitations",
    "No gym access", "Weather", "Self-consciousness", "Don't know where to start",
    "Lack of motivation", "No accountability partner"
  ];

  const toggleActivity = (activity) => {
    setSelectedActivities(prev => 
      prev.includes(activity) 
        ? prev.filter(a => a !== activity)
        : [...prev, activity]
    );
  };

  const toggleBarrier = (barrier) => {
    setSelectedBarriers(prev => 
      prev.includes(barrier) 
        ? prev.filter(b => b !== barrier)
        : [...prev, barrier]
    );
  };

  const setRating = (area, rating) => {
    setFitnessRatings(prev => ({ ...prev, [area]: rating }));
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold mb-4">
            Week 4 • Day 1 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Movement Assessment
          </h1>
          <p className="text-xl text-secondary-600">Know Where You Are to Get Where You're Going</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Activity className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Today's Task</h3>
              <p className="text-orange-100 text-lg">
                Rate yourself honestly in strength, flexibility, cardio, and balance. 
                No judgment—just awareness. Identify what you enjoy and what's been stopping you.
              </p>
            </div>
          </div>
        </section>

        {/* Fitness Assessment */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-2">Rate Yourself (1-10)</h2>
          <p className="text-secondary-600 mb-6">Click a number to rate yourself in each area. Be honest—this is just for you!</p>
          <div className="space-y-6">
            {fitnessAreas.map((item, index) => (
              <div key={index} className="bg-orange-50 rounded-xl p-5">
                <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-3 mb-3">
                  <h3 className="text-lg font-bold text-orange-700">{item.area}</h3>
                  <div className="flex gap-1 flex-wrap">
                    {[1,2,3,4,5,6,7,8,9,10].map(n => (
                      <button
                        key={n}
                        onClick={() => setRating(item.area, n)}
                        className={`w-8 h-8 border-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                          fitnessRatings[item.area] === n
                            ? 'bg-orange-500 border-orange-500 text-white scale-110 shadow-md'
                            : 'border-orange-300 text-orange-600 hover:bg-orange-100 hover:border-orange-400'
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
                <ul className="space-y-1 mb-3">
                  {item.questions.map((q, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-secondary-700">
                      <CheckCircle className="w-4 h-4 text-orange-400 flex-shrink-0" />
                      {q}
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-orange-600 italic">Why it matters: {item.why}</p>
                {fitnessRatings[item.area] && (
                  <div className="mt-2 text-sm font-medium text-orange-700">
                    Your rating: {fitnessRatings[item.area]}/10
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Activities You Enjoy */}
        <section className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-8 border-2 border-orange-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Heart className="w-6 h-6 text-orange-500" />
            Find What You Enjoy
          </h2>
          <p className="text-secondary-600 mb-4">
            Enjoyment is the #1 predictor of exercise adherence. <strong>Tap to select</strong> activities that sound fun to you:
          </p>
          <div className="grid md:grid-cols-2 gap-3">
            {enjoyableActivities.map((item, index) => {
              const isSelected = selectedActivities.includes(item.activity);
              return (
                <button
                  key={index}
                  onClick={() => toggleActivity(item.activity)}
                  className={`rounded-lg p-4 flex items-center gap-3 text-left transition-all duration-200 ${
                    isSelected
                      ? 'bg-orange-500 text-white shadow-lg scale-[1.02]'
                      : 'bg-white hover:bg-orange-100 hover:shadow-md'
                  }`}
                >
                  {isSelected ? (
                    <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-orange-500" />
                    </div>
                  ) : (
                    <Star className="w-5 h-5 text-orange-400 flex-shrink-0" />
                  )}
                  <div>
                    <span className={`font-medium ${isSelected ? 'text-white' : 'text-secondary-900'}`}>
                      {item.activity}
                    </span>
                    <p className={`text-xs ${isSelected ? 'text-orange-100' : 'text-secondary-500'}`}>
                      {item.benefits}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
          {selectedActivities.length > 0 && (
            <div className="mt-4 p-4 bg-white rounded-xl border-2 border-orange-300">
              <p className="text-sm font-semibold text-orange-700 mb-2">
                Your selected activities ({selectedActivities.length}):
              </p>
              <p className="text-secondary-700">{selectedActivities.join(', ')}</p>
            </div>
          )}
        </section>

        {/* Barriers */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Target className="w-6 h-6 text-orange-500" />
            Identify Your Barriers
          </h2>
          <p className="text-secondary-600 mb-4">What's stopped you from exercising? <strong>Tap all that apply:</strong></p>
          <div className="grid md:grid-cols-2 gap-3">
            {barriers.map((barrier, i) => {
              const isSelected = selectedBarriers.includes(barrier);
              return (
                <button
                  key={i}
                  onClick={() => toggleBarrier(barrier)}
                  className={`flex items-center gap-3 p-4 rounded-lg text-left transition-all duration-200 ${
                    isSelected
                      ? 'bg-orange-500 text-white shadow-md'
                      : 'bg-orange-50 text-secondary-700 hover:bg-orange-100'
                  }`}
                >
                  <div className={`w-5 h-5 border-2 rounded flex items-center justify-center flex-shrink-0 ${
                    isSelected ? 'bg-white border-white' : 'border-orange-300'
                  }`}>
                    {isSelected && <Check className="w-3 h-3 text-orange-500" />}
                  </div>
                  {barrier}
                </button>
              );
            })}
          </div>
          {selectedBarriers.length > 0 && (
            <div className="mt-4 p-4 bg-orange-50 rounded-xl border border-orange-200">
              <p className="text-sm font-semibold text-orange-700 mb-2">
                Your barriers ({selectedBarriers.length}):
              </p>
              <p className="text-secondary-700">{selectedBarriers.join(', ')}</p>
              <p className="text-sm text-orange-600 mt-2 italic">
                Awareness is the first step! Now you know what to work around.
              </p>
            </div>
          )}
          
          {/* Goal Setting */}
          <div className="mt-6 bg-gradient-to-r from-orange-100 to-red-100 rounded-xl p-6">
            <label className="block text-secondary-800 font-bold mb-3">
              ✨ Your movement goal for this week:
            </label>
            <textarea
              value={weekGoal}
              onChange={(e) => setWeekGoal(e.target.value)}
              placeholder="Example: I will take a 20-minute walk every morning before work, or I will do yoga 3 times this week..."
              className="w-full p-4 rounded-lg border-2 border-orange-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none text-secondary-800 min-h-[100px] resize-none"
            />
            {weekGoal && (
              <p className="mt-2 text-sm text-orange-700 font-medium">
                Great goal! Remember to make it specific and achievable.
              </p>
            )}
          </div>

          {/* Summary */}
          {(Object.values(fitnessRatings).some(v => v !== null) || selectedActivities.length > 0 || weekGoal) && (
            <div className="mt-6 p-6 bg-green-50 rounded-xl border-2 border-green-200">
              <h3 className="font-bold text-green-800 mb-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Your Assessment Summary
              </h3>
              {Object.values(fitnessRatings).some(v => v !== null) && (
                <div className="mb-3">
                  <p className="text-sm font-semibold text-green-700">Fitness Ratings:</p>
                  <p className="text-sm text-secondary-700">
                    {Object.entries(fitnessRatings)
                      .filter(([_, v]) => v !== null)
                      .map(([area, rating]) => `${area}: ${rating}/10`)
                      .join(' • ')}
                  </p>
                </div>
              )}
              {selectedActivities.length > 0 && (
                <div className="mb-3">
                  <p className="text-sm font-semibold text-green-700">Activities You'll Enjoy:</p>
                  <p className="text-sm text-secondary-700">{selectedActivities.join(', ')}</p>
                </div>
              )}
              {weekGoal && (
                <div>
                  <p className="text-sm font-semibold text-green-700">Your Week Goal:</p>
                  <p className="text-sm text-secondary-700">{weekGoal}</p>
                </div>
              )}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

export default MovementAssessmentGuide;
